import { useLegacyDialogProps } from 'components/DialogV3/DialogManagerContext';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogContentText,
    IconButton,
    InputAdornment,
    Typography,
} from '@mui/material';
import apiMcu from 'api/mcu';
import TextFieldVMP from 'components/common/TextField/TextFieldVMP';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { color } from 'theme';
import { setCloseDialogCC, toastError, toastSuccess } from 'utils/helpers';
import DialogBaseCC from './DialogBaseCC';
import { DIALOG_ID } from './constant';
import AutocompleteSearch from 'pages/mcu/components/AutocompleteSearch';
import { IconClose } from 'assets/icons';

function validate(key, value, t) {
    const regexInteger = /^[1-9]\d*$/;

    switch (key) {
        case 'name':
            if (value?.length === 0) {
                return {
                    value: true,
                    helperText: 'mcu.helperText.validate.nameRole.require',
                };
            }
            if (value?.length > 255) {
                return {
                    value: true,
                    helperText: 'mcu.helperText.validate.nameRole.max-length',
                };
            }
            return {
                value: false,
                helperText: '',
            };
        case 'code':
            if (value?.length === 0) {
                return {
                    value: true,
                    helperText: 'mcu.helperText.validate.deviceCode.require',
                };
            }
            if (value?.length > 64) {
                return {
                    value: true,
                    helperText: 'mcu.helperText.validate.deviceCode.max-length',
                };
            }
            return {
                value: false,
                helperText: '',
            };
        case 'brand':
        case 'model':
        case 'serial':
        case 'ip':
            if (value?.length > 64) {
                return {
                    value: true,
                    helperText: 'mcu.helperText.validate.optional.max-length',
                };
            }
            return {
                value: false,
                helperText: '',
            };
        case 'port':
            if (value?.length > 0) {
                if (!regexInteger.test(value)) {
                    return {
                        value: true,
                        helperText: 'mcu.helperText.validate.port.positiveNumber',
                    };
                }
            }
            if (value?.length > 5) {
                return {
                    value: true,
                    helperText: 'mcu.helperText.validate.port.max-length',
                };
            }
            return {
                value: false,
                helperText: '',
            };
        case 'link':
            if (value?.length > 255) {
                return {
                    value: true,
                    helperText: 'mcu.helperText.validate.link.max-length',
                };
            }
            return {
                value: false,
                helperText: '',
            };
        default:
            break;
    }
}

const DialogCreateOrUpdateMCUDevice = () => {
    const propsDialog = useLegacyDialogProps();
    const { title, titleButtonAction, titleButtonCancel, data, agreeAction } = propsDialog;
    const { open } = useSelector((state) => state.dialog);
    const { t } = useTranslation();
    const [errorWhenRoomIsProgressed, setErrorWhenRoomIsProgressed] = useState(false);

    const [newItem, setNewItem] = useState({
        name: '',
        code: '',
        brand: '',
        model: '',
        serial: '',
        room: '',
        sip: '',
        ip: '',
        port: '',
        link: ''
    });
    const handleValidateMCURoom = (data, t) => {
        const error = {
            name: { value: false, helperText: '' },
            code: { value: false, helperText: '' },
            brand: { value: false, helperText: '' },
            model: { value: false, helperText: '' },
            serial: { value: false, helperText: '' },
            // room: { value: false, helperText: '' },
            // sip: { value: false, helperText: '' },
            ip: { value: false, helperText: '' },
            port: { value: false, helperText: '' },
            link: { value: false, helperText: '' }
        };

        Object.keys(error).forEach((key) => (error[key] = validate(key, data[key], t)));
        return error;
    };
    const [errorValid, setErrorValid] = useState(() => handleValidateMCURoom(newItem, t));
    const [clickSubmit, setClickSubmit] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    const apiCreateMCUDevice = (body) => {
        const convertBody = {
            code: body.code,
            name: body.name,
            vendor: body.brand,
            model: body.model,
            serial_number: body.serial,
            sip_account_id: body.sip,
            preferred_room_id: body.room,
            mgmt_ip: body.ip,
            mgmt_port: body.port,
            mgmt_url: body.link
        }
        const cleanedData = Object.fromEntries(
            Object.entries(convertBody).filter(([_, value]) => value !== "")
        );
        return apiMcu.createDevice(cleanedData);
    };

    const apiUpdateDevice = (body, id) => {
        const convertBody = {
            code: body.code,
            name: body.name,
            vendor: body.brand,
            model: body.model,
            serial_number: body.serial,
            sip_account_id: body.sip,
            preferred_room_id: body.room,
            mgmt_ip: body.ip,
            mgmt_port: body.port,
            mgmt_url: body.link
        }
        const cleanedData = Object.fromEntries(
            Object.entries(convertBody).filter(([_, value]) => value !== "")
        );
        return apiMcu.updateDevice(cleanedData, id);
    }

    const handleCreateMCURoom = (newItem) => {
        if (title !== t('mcu.dialog.title.editDevice')) {
            apiCreateMCUDevice(newItem)
                .then((result) => {
                    toastSuccess(t('mcu.toast.createDeviceSuccessfully'))
                    setTimeout(() => {
                        agreeAction();
                    }, 100)
                })
                .catch((error) => {
                    if(error?.response?.data?.code === "DEVICE_CODE_EXISTS"){
                        toastError(t('mcu.toast.exitCodeDevice'));
                    } else toastError(t('mcu.toast.error'))
                })
                .finally(() => {
                    setIsUpdating(false)
                    handleCloseDialog();
                });
        } else {
            apiUpdateDevice(newItem, newItem?.id)
                .then((result) => {
                    toastSuccess(t('mcu.toast.updateDeviceSuccessfully'))
                    setTimeout(() => {
                        agreeAction();
                    }, 100)
                })
                .catch(error => {
                    toastError(t('mcu.toast.error'))
                    setIsUpdating(false)
                    handleCloseDialog();
                })
                .finally(() => {
                    setIsUpdating(false)
                    handleCloseDialog();
                });;
        }
    };

    const handleCloseDialog = () => {
        setCloseDialogCC();
        setNewItem({
            name: '',
            code: '',
            brand: '',
            model: '',
            serial: '',
            room: '',
            sip: '',
            ip: '',
            port: '',
            link: ''
        });
    };

    const handleAction = () => {
        setClickSubmit(true);
        setIsUpdating(true);
        if (errorValid['name']?.value) {
            setIsUpdating(false);
        } else if (errorValid['code']?.value) {
            setIsUpdating(false);
        } else if (errorValid['brand']?.value || errorValid['model']?.value || errorValid['serial']?.value || errorValid['room']?.value || errorValid['sip']?.value || errorValid['ip']?.value || errorValid['port']?.value || errorValid['link']?.value) {
            setIsUpdating(false);
        } else {
            handleCreateMCURoom(newItem);
        }
    };

    useEffect(() => {
        setErrorValid((_) => handleValidateMCURoom(newItem, t));
        // eslint-disable-next-line
    }, [newItem]);

    useEffect(() => {
        if (data) {
            setNewItem({
                id: data?.id,
                name: data?.name,
                code: data?.code,
                brand: data?.brand,
                model: data?.model,
                serial: data?.serial,
                room: data?.room,
                sip: data?.sip,
                ip: data?.ip,
                port: data?.port,
                link: data?.link,
                nameRoom: data?.nameRoom,
                nameSip: data?.nameSip
            });
        }
    }, [data]);

    useEffect(() => {
        if (!open) {
            setNewItem({
                name: "",
                code: "",
                brand: "",
                model: "",
                serial: "",
                room: "",
                sip: "",
                ip: "",
                port: "",
                link: ""
            });
            setErrorValid({
                name: { value: false, helperText: '' },
                code: { value: false, helperText: '' },
                brand: { value: false, helperText: '' },
                model: { value: false, helperText: '' },
                serial: { value: false, helperText: '' },
                // room: { value: false, helperText: '' },
                // sip: { value: false, helperText: '' },
                ip: { value: false, helperText: '' },
                port: { value: false, helperText: '' },
                link: { value: false, helperText: '' }
            });
            setClickSubmit(false);
            setErrorWhenRoomIsProgressed(false);
        }
    }, [open]);

    return (
        <DialogBaseCC id={DIALOG_ID.CREATE_OR_UPDATE_MCU_DEVICE} title={title}>
            <DialogContent sx={{ minWidth: '500px !important' }}>
                <DialogContentText>
                    <Box>
                        <Box sx={{ marginTop: { xs: '0.5rem', xl: '1rem' } }}>
                            <Typography sx={{ color: color.black }}>
                                {t('mcu.dialog.label.deviceCode')}<span style={{ color: color.main }}>*</span>
                            </Typography>
                            <TextFieldVMP
                                placeholder={t('mcu.dialog.placeholder.enterDeviceCode')}
                                autoFocus
                                error={clickSubmit && errorValid['code']?.value}
                                helperText={clickSubmit && t(errorValid['code']?.helperText)}
                                value={newItem.code}
                                onChange={(e) => {
                                    setNewItem({
                                        ...newItem,
                                        code: e.target.value.trim(),
                                    });
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {newItem.code !== '' ? (
                                                <IconButton
                                                    tabIndex={-1}
                                                    aria-label="toggle password visibility"
                                                    onClick={() => {
                                                        setNewItem({
                                                            ...newItem,
                                                            code: '',
                                                        });
                                                    }}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    <IconClose />
                                                </IconButton>
                                            ) : (
                                                <></>
                                            )}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <Box sx={{ marginTop: { xs: '0.5rem', xl: '1rem' } }}>
                            <Typography sx={{ color: color.black }}>
                                {t('mcu.dialog.label.deviceName')}<span style={{ color: color.main }}>*</span>
                            </Typography>
                            <TextFieldVMP
                                placeholder={t('mcu.dialog.placeholder.enterDeviceName')}
                                error={clickSubmit && errorValid['name']?.value}
                                helperText={clickSubmit && t(errorValid['name']?.helperText)}
                                value={newItem.name}
                                onChange={(e) => {
                                    setNewItem({
                                        ...newItem,
                                        name: e.target.value,
                                    });
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {newItem.name !== '' ? (
                                                <IconButton
                                                    tabIndex={-1}
                                                    aria-label="toggle password visibility"
                                                    onClick={() => {
                                                        setNewItem({
                                                            ...newItem,
                                                            name: '',
                                                        });
                                                    }}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    <IconClose />
                                                </IconButton>
                                            ) : (
                                                <></>
                                            )}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <Box sx={{ marginTop: { xs: '0.5rem', xl: '1rem' } }}>
                            <Typography sx={{ color: color.black }}>
                                {t('mcu.dialog.label.brand')}
                            </Typography>
                            <TextFieldVMP
                                placeholder={t('mcu.dialog.placeholder.enterBrand')}
                                error={clickSubmit && errorValid['brand']?.value}
                                helperText={clickSubmit && t(errorValid['brand']?.helperText)}
                                value={newItem.brand}
                                onChange={(e) => {
                                    setNewItem({
                                        ...newItem,
                                        brand: e.target.value,
                                    });
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {newItem.brand !== '' ? (
                                                <IconButton
                                                    tabIndex={-1}
                                                    aria-label="toggle password visibility"
                                                    onClick={() => {
                                                        setNewItem({
                                                            ...newItem,
                                                            brand: '',
                                                        });
                                                    }}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    <IconClose />
                                                </IconButton>
                                            ) : (
                                                <></>
                                            )}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <Box sx={{ marginTop: { xs: '0.5rem', xl: '1rem' } }}>
                            <Typography sx={{ color: color.black }}>
                                {t('mcu.dialog.label.model')}
                            </Typography>
                            <TextFieldVMP
                                placeholder={t('mcu.dialog.placeholder.enterModel')}
                                error={clickSubmit && errorValid['model']?.value}
                                helperText={clickSubmit && t(errorValid['model']?.helperText)}
                                value={newItem.model}
                                onChange={(e) => {
                                    setNewItem({
                                        ...newItem,
                                        model: e.target.value,
                                    });
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {newItem.model !== '' ? (
                                                <IconButton
                                                    tabIndex={-1}
                                                    aria-label="toggle password visibility"
                                                    onClick={() => {
                                                        setNewItem({
                                                            ...newItem,
                                                            model: '',
                                                        });
                                                    }}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    <IconClose />
                                                </IconButton>
                                            ) : (
                                                <></>
                                            )}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <Box sx={{ marginTop: { xs: '0.5rem', xl: '1rem' } }}>
                            <Typography sx={{ color: color.black }}>
                                {t('mcu.dialog.label.serial')}
                            </Typography>
                            <TextFieldVMP
                                placeholder={t('mcu.dialog.placeholder.enterSerial')}
                                error={clickSubmit && errorValid['serial']?.value}
                                helperText={clickSubmit && t(errorValid['serial']?.helperText)}
                                value={newItem.serial}
                                onChange={(e) => {
                                    setNewItem({
                                        ...newItem,
                                        serial: e.target.value,
                                    });
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {newItem.serial !== '' ? (
                                                <IconButton
                                                    tabIndex={-1}
                                                    aria-label="toggle password visibility"
                                                    onClick={() => {
                                                        setNewItem({
                                                            ...newItem,
                                                            serial: '',
                                                        });
                                                    }}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    <IconClose />
                                                </IconButton>
                                            ) : (
                                                <></>
                                            )}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <Box sx={{ marginTop: { xs: '0.5rem', xl: '1rem' } }}>
                            <Typography sx={{ color: color.black }}>
                                {t('mcu.dialog.label.room')}
                            </Typography>
                            <AutocompleteSearch
                                searchFor={1}
                                nameDisplay={newItem?.nameRoom}
                                placeholder={t('mcu.placeholder.enterRoomName')}
                                setValue={(value) => {
                                    setNewItem({
                                        ...newItem,
                                        room: value.id,
                                    });
                                }}
                            />
                        </Box>
                        <Box sx={{ marginTop: { xs: '0.5rem', xl: '1rem' } }}>
                            <Typography sx={{ color: color.black }}>
                                {t('mcu.dialog.label.sipAccount')}
                            </Typography>
                            <AutocompleteSearch
                                searchFor={2}
                                nameDisplay={newItem?.nameSip}
                                placeholder={t('mcu.placeholder.enterSipName')}
                                setValue={(value) => {
                                    setNewItem({
                                        ...newItem,
                                        sip: value.id,
                                    });
                                }}
                            />
                        </Box>
                        <Box sx={{ marginTop: { xs: '0.5rem', xl: '1rem' } }}>
                            <Typography sx={{ color: color.black }}>
                                {t('mcu.dialog.label.ip')}
                            </Typography>
                            <TextFieldVMP
                                placeholder={t('mcu.dialog.placeholder.enterIp')}
                                error={clickSubmit && errorValid['ip']?.value}
                                helperText={clickSubmit && t(errorValid['ip']?.helperText)}
                                value={newItem.ip}
                                onChange={(e) => {
                                    setNewItem({
                                        ...newItem,
                                        ip: e.target.value,
                                    });
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {newItem.ip !== '' ? (
                                                <IconButton
                                                    tabIndex={-1}
                                                    aria-label="toggle password visibility"
                                                    onClick={() => {
                                                        setNewItem({
                                                            ...newItem,
                                                            ip: '',
                                                        });
                                                    }}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    <IconClose />
                                                </IconButton>
                                            ) : (
                                                <></>
                                            )}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <Box sx={{ marginTop: { xs: '0.5rem', xl: '1rem' } }}>
                            <Typography sx={{ color: color.black }}>
                                {t('mcu.dialog.label.port')}
                            </Typography>
                            <TextFieldVMP
                                placeholder={t('mcu.dialog.placeholder.enterPort')}
                                error={clickSubmit && errorValid['port']?.value}
                                helperText={clickSubmit && t(errorValid['port']?.helperText)}
                                value={newItem.port}
                                onChange={(e) => {
                                    setNewItem({
                                        ...newItem,
                                        port: e.target.value,
                                    });
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {newItem.port !== '' ? (
                                                <IconButton
                                                    tabIndex={-1}
                                                    aria-label="toggle password visibility"
                                                    onClick={() => {
                                                        setNewItem({
                                                            ...newItem,
                                                            port: '',
                                                        });
                                                    }}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    <IconClose />
                                                </IconButton>
                                            ) : (
                                                <></>
                                            )}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <Box sx={{ marginTop: { xs: '0.5rem', xl: '1rem' } }}>
                            <Typography sx={{ color: color.black }}>
                                {t('mcu.dialog.label.link')}
                            </Typography>
                            <TextFieldVMP
                                placeholder={t('mcu.dialog.placeholder.enterLink')}
                                error={clickSubmit && errorValid['link']?.value}
                                helperText={clickSubmit && t(errorValid['link']?.helperText)}
                                value={newItem.link}
                                onChange={(e) => {
                                    setNewItem({
                                        ...newItem,
                                        link: e.target.value,
                                    });
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {newItem.link !== '' ? (
                                                <IconButton
                                                    tabIndex={-1}
                                                    aria-label="toggle password visibility"
                                                    onClick={() => {
                                                        setNewItem({
                                                            ...newItem,
                                                            link: '',
                                                        });
                                                    }}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    <IconClose />
                                                </IconButton>
                                            ) : (
                                                <></>
                                            )}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    </Box>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    onClick={() => {
                        handleCloseDialog();
                    }}
                >
                    {titleButtonCancel}
                </Button>
                <LoadingButton
                    variant="contained"
                    loading={isUpdating}
                    onClick={() => {
                        handleAction();
                    }}
                    disabled={errorWhenRoomIsProgressed || newItem?.name?.length === 0 || newItem?.code?.length === 0}
                >
                    {titleButtonAction}
                </LoadingButton>
            </DialogActions>
        </DialogBaseCC>
    );
};

export default DialogCreateOrUpdateMCUDevice;
